import MethodMultitest from "~/schema/db/public/MethodMultitest";
import type { RepositoryId } from "~/schema/db/public/Repository";
import StatisticalTest from "~/schema/db/public/StatisticalTest";
import type { UserId } from "~/schema/db/public/User";

export const SYNTHESIZE_BIO_REPOSITORY_ID =
  "00000000-0000-0000-0000-000000000001" as RepositoryId;

export const SYNTHESIZE_FEATURED_DATASETS_REPOSITORY_ID =
  "00000000-0000-0000-0000-000000000005" as RepositoryId;

export const CELLXGENE_REPOSITORY_ID = "00000000-0000-0000-0000-000000000006" as RepositoryId;
export const SYSTEM_USER_ID = "00000000-0000-0000-0000-000000000000" as UserId;
export const DEMO_ANALYSES_USER_ID = "00000000-0000-0000-0000-000000000001" as UserId;
export const GUEST_USER_ID = "00000000-0000-0000-0000-000000000007" as UserId;
export const API_INTEGRATION_TEST_USER_ID = "00000000-0000-0000-0000-000000000008" as UserId;

// Reviewer demo accounts for external app-store / connector-directory review
// teams (OpenAI ChatGPT Apps Directory, Microsoft Copilot Connector Directory).
// Backed by DEMO_ACCOUNT_* env vars, NOT by an inbox. See
// `app/lib/demoAccount.server.ts` and `app/routes/api/mcp/REVIEWER_ACCOUNT.md`.
export const OPENAI_REVIEWER_DEMO_USER_ID = "00000000-0000-0000-0000-000000000009" as UserId;
/** @deprecated Prefer {@link OPENAI_REVIEWER_DEMO_USER_ID}. */
export const REVIEWER_DEMO_USER_ID = OPENAI_REVIEWER_DEMO_USER_ID;
export const MICROSOFT_REVIEWER_DEMO_USER_ID =
  "00000000-0000-0000-0000-00000000000a" as UserId;

// HACK: This allows joins to user_dataset but no users will ever match this
export const DOES_NOT_MATCH_ANY_USER_ID = "ffffffff-ffff-ffff-ffff-ffffffffffff" as UserId;

export const UUID_REGEX_STRING =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

export const ONE_SECOND_MS = 1000;
export const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
export const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;

export const TOTP_MAX_AGE_SECONDS = 60 * 30;

export const MAX_CHUNK_EXECUTION_TIME_MINUTES = 20;
export const SANDBOX_INACTIVE_TIMEOUT_MS = 10 * ONE_MINUTE_MS;
export const SANDBOX_KEEP_ALIVE_INTERVAL_MS = Math.min(
  2 * ONE_MINUTE_MS,
  0.25 * SANDBOX_INACTIVE_TIMEOUT_MS,
);

export const multitestOptions: Record<MethodMultitest, string> = {
  [MethodMultitest.bonferroni]: "Bonferroni correction",
  [MethodMultitest.sidak]: "Šidák correction",
  [MethodMultitest["holm-sidak"]]: "Holm-Šidák method",
  [MethodMultitest.holm]: "Holm-Bonferroni method",
  [MethodMultitest["simes-hochberg"]]: "Simes-Hochberg method",
  [MethodMultitest.hommel]: "Hommel's method",
  [MethodMultitest.fdr_bh]: "Benjamini-Hochberg procedure",
};

export const statisticalTestOptions: Record<StatisticalTest, string> = {
  [StatisticalTest.proportion_z]: "two-sample proportion z-test",
  [StatisticalTest.chi2]: "chi-squared test",
  [StatisticalTest.fisher]: "Fisher's exact test",
  [StatisticalTest.sekhon]: "Sekhon's Bayesian alternative to Fisher's",
  [StatisticalTest.wilcox]: "Mann-Whitney U test",
  [StatisticalTest.ks]: "Kolmogorov-Smirnov test",
  [StatisticalTest.t]: "t-test",
  [StatisticalTest.deseq2]: "DESeq2",
};

export const ANALYSIS_STATE_KEY = "q";

export type QCMetric = (typeof QC_METRICS)[number];
export type QCFlag = (typeof QC_FLAGS)[number];

export const QC_METRICS = [
  "totalReadsBeforeFiltering",
  "totalBasesBeforeFiltering",
  "q20BasesBeforeFiltering",
  "q30BasesBeforeFiltering",
  "q20RateBeforeFiltering",
  "q30RateBeforeFiltering",
  "read1MeanLengthBeforeFiltering",
  "read2MeanLengthBeforeFiltering",
  "gcContentBeforeFiltering",
  "totalReadsAfterFiltering",
  "totalBasesAfterFiltering",
  "q20BasesAfterFiltering",
  "q30BasesAfterFiltering",
  "q20RateAfterFiltering",
  "q30RateAfterFiltering",
  "read1MeanLengthAfterFiltering",
  "read2MeanLengthAfterFiltering",
  "gcContentAfterFiltering",
  "passedFilterReads",
  "lowQualityReads",
  "tooManyNReads",
  "tooShortReads",
  "tooLongReads",
  "rateDuplication",
  "fragLength",
  "palignedReads",
  "totalReads",
  "rateAligned",
  "nonzeroGenes",
] as const;

export const QC_FLAGS = [
  "duplicationFlag",
  "alignmentFlag",
  "detectedGenesFlag",
  "qcFlag",
] as const;

export const brandColors = {
  blue: "#405FFF",
  red: "#FF405F",
  purple: "#A070E8",
  citron: "#E5FB6B",
  black: "#060606",
  grey: "#F5F7FF",
} as const;

export const harmonizedMetadataColumns = [
  "sampleType",
  "perturbationType",
  "tissueName",
  "diseaseName",
  "cellTypeName",
  "cellLineName",
  "perturbationName",
  "sex",
  "ageYears",
  "cellCount",
] as const;

export interface LambdaResponse {
  statusCode: number;
  body: {
    error?: string;
    traceback?: string;
  };
}
